import { useState, useMemo } from 'react';

import styled from 'styled-components';
import Lottie from 'lottie-react';
import XLSX from 'sheetjs-style';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import PointIssueIcon from '../../../image/PointIssueIcon.svg';
import PointUseIcon from '../../../image/PointUseIcon.svg';
import PointHoldIcon from '../../../image/PointHoldIcon.svg';
import PointDocsIcon from '../../../image/PointDocsIcon.svg';
import DefaultProfile from '../../../image/DefaultProfile.png';
import NormalSortButtonPath from '../../../image/GrayNormalSortButton.svg';
import NormalSortButtonDarkPath from '../../../image/GrayNormalSortButtonDark.svg';
import AscSortButtonPath from '../../../image/AscSortButton2.svg';
import AscSortButtonDarkPath from '../../../image/AscSortButtonDark2.svg';
import DescSortButtonPath from '../../../image/DescSortButton2.svg';
import DescSortButtonDarkPath from '../../../image/DescSortButtonDark2.svg';

import LoadingAnimation from '../../../Animation/NewLoading.json';

// 탭 종류 (보유 / 발행 내역 / 사용 내역) //
const TAB_KEYS = { HOLDING: 'holding', ISSUE: 'issue', USE: 'use' };

// 보유 탭 정렬 컬럼 //
const SORT_COLUMNS = { DATE: 'date', BALANCE: 'balance', ACCUMULATED: 'accumulated', USED: 'used' };

// 백엔드 holdings 응답 필드 매핑 (현재보유 = 누적발행 − 누적사용으로 정합) //
const HOLDING_BALANCE_FIELD = 'totalPoint';
const HOLDING_ACCUMULATED_FIELD = 'cumulativeIssuedPoint';
const HOLDING_USED_FIELD = 'cumulativeUsedPoint';

export default function PointManagementBox(props) {
  const {
    calcDateText,
    pointData,
    selectedPartnerGym,
    isOpenPartnerGymSales,
    isPointLoading,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState(TAB_KEYS.ISSUE);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('normal');

  // pointData 기본값 방어 (요약 카드) //
  const totalIssued = pointData?.totalIssuedPoint ?? 0;
  const totalUsed = pointData?.totalUsedPoint ?? 0;
  const currentHeld = pointData?.totalHeldPoint ?? 0;

  // 현재 탭 목록 (histories는 부호로 발행/사용 분기) + 컬럼 정렬 적용 //
  const currentList = useMemo(() => {
    // 보유 탭: holdings (서버 totalPoint 내림차순 정렬 완료) //
    if (activeTab === TAB_KEYS.HOLDING) {
      const source = pointData?.holdings ?? [];

      if (sortColumn === '' || sortDirection === 'normal') return source;

      const field =
        sortColumn === SORT_COLUMNS.ACCUMULATED ? HOLDING_ACCUMULATED_FIELD :
        sortColumn === SORT_COLUMNS.USED ? HOLDING_USED_FIELD : HOLDING_BALANCE_FIELD;

      return [...source].sort((a, b) => {
        const diff = (a[field] ?? 0) - (b[field] ?? 0);
        return sortDirection === 'asc' ? diff : -diff;
      });
    }

    // 발행/사용 탭: histories 부호 필터 (서버 approvalDate 내림차순 정렬 완료) //
    const histories = pointData?.histories ?? [];
    const source = activeTab === TAB_KEYS.USE ? histories.filter(h => h.point < 0) : histories.filter(h => h.point > 0);

    if (sortColumn !== SORT_COLUMNS.DATE || sortDirection === 'normal') return source;

    return [...source].sort((a, b) => {
      const cmp = (a.approvalDate || '').localeCompare(b.approvalDate || '');
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [activeTab, sortColumn, sortDirection, pointData]);

  // 탭 전환 (정렬 초기화) //
  function onClickTab(tabKey) {
    setActiveTab(tabKey);
    setSortColumn('');
    setSortDirection('normal');
  }

  // 컬럼 정렬 토글 (normal → asc → desc → normal), 다른 컬럼 클릭 시 해당 컬럼 asc //
  function onClickSort(column) {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('asc');
      return;
    }

    if (sortDirection === 'asc') setSortDirection('desc');
    else if (sortDirection === 'desc') {
      setSortColumn('');
      setSortDirection('normal');
    }
    else setSortDirection('asc');
  }

  // 정렬 버튼 이미지 (활성 컬럼만 asc/desc, 나머지는 기본) //
  function getSortButtonImg(column) {
    if (sortColumn === column && sortDirection === 'asc') return isDark ? AscSortButtonDarkPath : AscSortButtonPath;
    if (sortColumn === column && sortDirection === 'desc') return isDark ? DescSortButtonDarkPath : DescSortButtonPath;
    return isDark ? NormalSortButtonDarkPath : NormalSortButtonPath;
  }

  // 숫자 콤마 포맷팅 //
  function formatNumber(num) {
    return (num ?? 0).toLocaleString('ko-KR');
  }

  // 포인트 부호 표시 (발행 +, 사용 -, 보유/누적 무부호) //
  function decideSignedPoint(point) {
    const abs = formatNumber(Math.abs(point ?? 0));

    if (activeTab === TAB_KEYS.USE) return '- ' + abs;
    return '+ ' + abs;
  }

  // 날짜 포맷 (YY.MM.DD) //
  function formatShortDate(iso) {
    if (!iso) return '';

    const d = iso.split('T')[0].split('-');
    const order = t('statistics.calendar.dateOrder');
    const sep = t('statistics.calendar.dateSeparator');

    if (order === 'MDY') return d[1] + sep + d[2] + sep + d[0].slice(2);
    return d[0].slice(2) + sep + d[1] + sep + d[2];
  }

  // 회원 상세로 이동 //
  function onClickRow(rowData) {
    if (rowData.memberId === undefined) return;

    if (selectedPartnerGym === undefined || selectedPartnerGym.isMyStore) {
      window.open('/member/valid/detail/' + rowData.memberId, '_blank');
    }
    else {
      window.open('/member/partner_gym/detail/' + rowData.memberId, '_blank');
    }
  }

  // 이미지 로드 실패시 기본 프로필로 대체 //
  function onErrorProfileImage(e) {
    e.target.src = DefaultProfile;
  }

  // 현재 탭 리스트를 엑셀로 추출 //
  function onClickExport() {
    if (currentList.length === 0) {
      alert(t('statistics.point.exportNoData'));
      return;
    }

    const rows = [];

    if (activeTab === TAB_KEYS.HOLDING) {
      for (let i = 0; i < currentList.length; i++) {
        const data = currentList[i];

        rows.push({
          [t('statistics.point.colRank')]: i + 1,
          [t('statistics.point.colName')]: data.memberName,
          [t('statistics.point.colBalance')]: data[HOLDING_BALANCE_FIELD] ?? 0,
          [t('statistics.point.colAccumulated')]: data[HOLDING_ACCUMULATED_FIELD] ?? 0,
          [t('statistics.point.colUsedTotal')]: data[HOLDING_USED_FIELD] ?? 0,
        });
      }
    }
    else if (activeTab === TAB_KEYS.USE) {
      for (const data of currentList) {
        rows.push({
          [t('statistics.point.colName')]: data.memberName,
          [t('statistics.point.colUsePoint')]: data.point,
          [t('statistics.point.colUseReason')]: data.reason,
          [t('statistics.point.colUseDate')]: formatShortDate(data.approvalDate),
        });
      }
    }
    else {
      for (const data of currentList) {
        rows.push({
          [t('statistics.point.colName')]: data.memberName,
          [t('statistics.point.colIssuePoint')]: data.point,
          [t('statistics.point.colIssueReason')]: data.reason,
          [t('statistics.point.colIssueDate')]: formatShortDate(data.approvalDate),
        });
      }
    }

    const tabLabel =
      activeTab === TAB_KEYS.HOLDING ? t('statistics.point.tabHolding') :
      activeTab === TAB_KEYS.USE ? t('statistics.point.tabUse') : t('statistics.point.tabIssue');

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, tabLabel);
    XLSX.writeFile(wb, t('statistics.point.title') + '_' + tabLabel + '.xlsx');
  }

  // 포인트 값 셀 (숫자 + P 접미사) //
  function renderPointValue(value, isSigned) {
    return (
      <PointText>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
          {isSigned ? decideSignedPoint(value) : formatNumber(value)}
        </Text>
        <PointSuffix>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.textTertiary}>P</Text>
        </PointSuffix>
      </PointText>
    );
  }

  // 정렬 가능한 헤더 라벨 (라벨 + 정렬 버튼) //
  function renderSortLabel(label, column) {
    return (
      <SortLabelWrapper>
        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{label}</Text>
        <SortButton onClick={() => onClickSort(column)}>
          <SortIcon src={getSortButtonImg(column)} />
        </SortButton>
      </SortLabelWrapper>
    );
  }

  // 상단 좌측 요약 카드 렌더링 //
  function renderSummaryCard(icon, label, value) {
    return (
      <SummaryCard $isDark={isDark}>
        <SummaryLabelRow>
          <SummaryIcon src={icon} />
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{label}</Text>
        </SummaryLabelRow>
        <SummaryValueRow>
          <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>{formatNumber(value)}</Text>
          <PointSuffix>
            <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={theme.text}>P</Text>
          </PointSuffix>
        </SummaryValueRow>
      </SummaryCard>
    );
  }

  // 순위 배지 (다크: 1위 500 / 2위 600 / 3위 700, 라이트: 1위 500 / 2위 400 / 3위 300) //
  function renderRankBadge(rank) {
    const isTop3 = rank <= 3;

    let rankColors = [theme.brand500, theme.brand600, theme.brand700];

    if (!isDark) rankColors = [theme.brand500, theme.brand400, theme.brand300];

    const badgeColor = isTop3 ? rankColors[rank - 1] : 'transparent';

    return (
      <RankBadge $bgColor={badgeColor}>
        <Text fontFamily={'NotoSansKR-Bold'} fontSize={16} color={isTop3 ? theme.background : theme.subText}>
          {rank}
        </Text>
      </RankBadge>
    );
  }

  // 보유 탭 헤더 렌더링 //
  function renderHoldingHeader() {
    return (
      <TableHeaderRow>
        <RankCell>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.point.colRank')}</Text>
        </RankCell>
        <ProfileCell />
        <NameCell>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.point.colName')}</Text>
        </NameCell>
        <NumberHeaderCell $width={130}>
          {renderSortLabel(t('statistics.point.colBalance'), SORT_COLUMNS.BALANCE)}
        </NumberHeaderCell>
        <NumberHeaderCell $width={130}>
          {renderSortLabel(t('statistics.point.colAccumulated'), SORT_COLUMNS.ACCUMULATED)}
        </NumberHeaderCell>
        <NumberHeaderCell $width={130}>
          {renderSortLabel(t('statistics.point.colUsedTotal'), SORT_COLUMNS.USED)}
          <DocsIcon src={PointDocsIcon} onClick={onClickExport} />
        </NumberHeaderCell>
      </TableHeaderRow>
    );
  }

  // 발행/사용 탭 헤더 렌더링 //
  function renderHistoryHeader() {
    const pointLabel = activeTab === TAB_KEYS.USE ? t('statistics.point.colUsePoint') : t('statistics.point.colIssuePoint');
    const reasonLabel = activeTab === TAB_KEYS.USE ? t('statistics.point.colUseReason') : t('statistics.point.colIssueReason');
    const dateLabel = activeTab === TAB_KEYS.USE ? t('statistics.point.colUseDate') : t('statistics.point.colIssueDate');

    return (
      <TableHeaderRow>
        <ProfileCell />
        <NameCell>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.point.colName')}</Text>
        </NameCell>
        <NumberHeaderCell $width={120}>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{pointLabel}</Text>
        </NumberHeaderCell>
        <NumberHeaderCell $width={220}>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{reasonLabel}</Text>
        </NumberHeaderCell>
        <NumberHeaderCell $width={130}>
          {renderSortLabel(dateLabel, SORT_COLUMNS.DATE)}
          <DocsIcon src={PointDocsIcon} onClick={onClickExport} />
        </NumberHeaderCell>
      </TableHeaderRow>
    );
  }

  // 보유 탭 행 렌더링 //
  function renderHoldingRow(rowData, index) {
    return (
      <TableBodyRow key={'PointRow-' + index} onClick={() => onClickRow(rowData)}>
        <RankCell>
          {renderRankBadge(index + 1)}
        </RankCell>
        <ProfileCell>
          <ProfileImage src={rowData.profileUrl || DefaultProfile} onError={onErrorProfileImage} />
        </ProfileCell>
        <NameCell>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{rowData.memberName}</Text>
        </NameCell>
        <NumberBodyCell $width={130}>
          {renderPointValue(rowData[HOLDING_BALANCE_FIELD], false)}
        </NumberBodyCell>
        <NumberBodyCell $width={130}>
          {renderPointValue(rowData[HOLDING_ACCUMULATED_FIELD], false)}
        </NumberBodyCell>
        <NumberBodyCell $width={130}>
          {renderPointValue(rowData[HOLDING_USED_FIELD], false)}
        </NumberBodyCell>
      </TableBodyRow>
    );
  }

  // 발행/사용 탭 행 렌더링 //
  function renderHistoryRow(rowData, index) {
    return (
      <TableBodyRow key={'PointRow-' + index} onClick={() => onClickRow(rowData)}>
        <ProfileCell>
          <ProfileImage src={rowData.profileUrl || DefaultProfile} onError={onErrorProfileImage} />
        </ProfileCell>
        <NameCell>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{rowData.memberName}</Text>
        </NameCell>
        <NumberBodyCell $width={120}>
          {renderPointValue(rowData.point, true)}
        </NumberBodyCell>
        <NumberBodyCell $width={220}>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{rowData.reason}</Text>
        </NumberBodyCell>
        <NumberBodyCell $width={130}>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{formatShortDate(rowData.approvalDate)}</Text>
        </NumberBodyCell>
      </TableBodyRow>
    );
  }

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isPointLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <ContentWrapper>
              <TitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {calcDateText()}
                </Text>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {t('statistics.point.title')}
                </Text>
              </TitleRow>

              <TabRow>
                <TabWrapper $isDark={isDark}>
                  <TabButton $isActive={activeTab === TAB_KEYS.HOLDING} onClick={() => onClickTab(TAB_KEYS.HOLDING)}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={activeTab === TAB_KEYS.HOLDING ? theme.subBackground : theme.text}>
                      {t('statistics.point.tabHolding')}
                    </Text>
                  </TabButton>
                  <TabButton $isActive={activeTab === TAB_KEYS.ISSUE} onClick={() => onClickTab(TAB_KEYS.ISSUE)}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={activeTab === TAB_KEYS.ISSUE ? theme.subBackground : theme.text}>
                      {t('statistics.point.tabIssue')}
                    </Text>
                  </TabButton>
                  <TabButton $isActive={activeTab === TAB_KEYS.USE} onClick={() => onClickTab(TAB_KEYS.USE)}>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={activeTab === TAB_KEYS.USE ? theme.subBackground : theme.text}>
                      {t('statistics.point.tabUse')}
                    </Text>
                  </TabButton>
                </TabWrapper>
              </TabRow>

              <BodyRow>
                <SummaryColumn>
                  {renderSummaryCard(PointIssueIcon, t('statistics.point.totalIssued'), totalIssued)}
                  {renderSummaryCard(PointUseIcon, t('statistics.point.totalUsed'), totalUsed)}
                  {renderSummaryCard(PointHoldIcon, t('statistics.point.currentHeld'), currentHeld)}
                </SummaryColumn>
                <TableColumn>
                  {activeTab === TAB_KEYS.HOLDING ? renderHoldingHeader() : renderHistoryHeader()}
                  <TableDividerLine />
                  <TableBodyWrapper>
                    {
                      activeTab === TAB_KEYS.HOLDING ?
                        currentList.map((rowData, index) => renderHoldingRow(rowData, index))
                        :
                        currentList.map((rowData, index) => renderHistoryRow(rowData, index))
                    }
                  </TableBodyWrapper>
                </TableColumn>
              </BodyRow>
            </ContentWrapper>
            :
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  margin: 39.5px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;

  gap: 10px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;
  height: 451px;

  gap: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: 100%;
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  width: 100%;
`;

const TabWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  height: 31px;
  padding: 5px;

  gap: 5px;

  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill};
`;

const TabButton = styled.div`
  display: flex;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 10px;

  width: 90px;
  height: 9px;

  border-radius: 6px;
  background-color: ${props => props.$isActive ? props.theme.brand500 : 'transparent'};

  cursor: pointer;

  & p {
    cursor: pointer;
  }
`;

const BodyRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;

  flex: 1;
  min-height: 0;

  width: 100%;

  gap: 20px;
`;

const SummaryColumn = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  width: 183px;

  gap: 20px;

  opacity: 0.8;
`;

const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  flex: 1;

  padding: 15px;

  width: 100%;

  gap: 10px;

  box-sizing: border-box;

  border-radius: 10px;
  box-shadow: ${props => props.$isDark ? 'none' : '0px 0px 15px rgba(0, 0, 0, 0.07)'};
  background-color: ${props => props.theme.thirdBackground};
`;

const SummaryLabelRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 5px;
`;

const SummaryIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const SummaryValueRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 5px;
`;

const PointSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const TableColumn = styled.div`
  display: flex;
  flex-direction: column;

  flex: 1;

  width: 55px;

  align-items: flex-start;
  overflow: hidden;

  min-width: 0;

  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};

  /* 전역 box-sizing reset이 없어 content-box 기본값이라 헤더·바디 컬럼 폭이 어긋남 방지 */
  * {
    box-sizing: border-box;
  }
`;

const TableHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  padding: 0 12px 0 10px;

  width: 100%;
  height: 30px;
`;

const ProfileCell = styled.div`
  display: flex;
  align-items: center;

  width: 50px;

  flex-shrink: 0;
`;

const RankCell = styled.div`
  display: flex;

  align-items: center;
  justify-content: flex-start;

  padding-left: 5px;

  width: 55px;

  flex-shrink: 0;
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  gap: 10px;
`;

const NumberHeaderCell = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  width: ${props => props.$width}px;

  flex-shrink: 0;
`;

const NumberBodyCell = styled.div`
  display: flex;

  align-items: center;
  justify-content: flex-start;
  overflow: hidden;

  width: ${props => props.$width}px;

  flex-shrink: 0;
`;

const SortLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 5px;
`;

const SortButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
`;

const SortIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const DocsIcon = styled.img`
  width: 24px;
  height: 24px;

  cursor: pointer;
`;

const RankBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 25px;
  height: 25px;

  border-radius: 4px;
  background-color: ${props => props.$bgColor};
`;

const TableDividerLine = styled.div`
  width: 100%;
  height: 1px;

  margin: 7px 0;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const TableBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  flex: 1;
  min-height: 0;

  width: 100%;

  overflow-y: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.brand500};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 200px;
  }
`;

const TableBodyRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex-shrink: 0;

  padding: 10px 0 10px 10px;

  width: 100%;

  border: 1px solid transparent;
  border-radius: 5px;

  cursor: pointer;

  & p {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border: 1px solid ${props => props.theme.brand500Alpha50};
    }
  }
`;

const ProfileImage = styled.img`
  width: 25px;
  height: 25px;

  border-radius: 4px;
  object-fit: cover;

  flex-shrink: 0;
`;

const PointText = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  gap: 2px;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  padding: 40px 0;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;
